/**
 * Subscription Service
 * Centralized functions for subscription-related API calls
 */

import api from './api';

/**
 * Get the current user's subscription status
 * @returns {Promise} API response with subscription data
 */
export const getStatus = () => {
  // Log the attempt to call the API
  console.log('🔍 DEBUG - Calling subscription status API');
  console.log('🔍 DEBUG - Current API baseURL:', api.defaults.baseURL);
  console.log('🔍 DEBUG - Request path:', '/subscription/status');
  
  // Make the API call and handle response/errors
  return api.get('/subscription/status')
    .then(response => {
      console.log('✅ Subscription API success:', response.status);
      return response;
    })
    .catch(error => {
      console.error('❌ Subscription API error:', error.message);
      console.error('❌ Full error:', error);
      throw error;
    });
};

/**
 * Get a Stripe customer portal session URL
 * @returns {Promise} API response with portal session URL
 */
export const getPortalSession = () => api.get('/subscription/portal');

/**
 * Create a checkout session for subscription upgrade
 * @returns {Promise} API response with checkout session URL
 */
export const createCheckoutSession = () => api.post('/subscription/create-checkout-session');

/**
 * Cancel the current subscription
 * @returns {Promise} API response with cancellation status
 */
export const cancelSubscription = () => api.post('/subscription/cancel');

/**
 * Verify a Stripe checkout session
 * @param {string} sessionId - The Stripe checkout session ID to verify
 * @returns {Promise} API response with verification status
 */
export const verifySession = (sessionId) => api.post('/subscription/verify-session', { sessionId });

/**
 * Get invoices for the current user
 * @returns {Promise} API response with invoices data
 */
export const getInvoices = () => api.get('/invoices');

/**
 * Download a specific invoice
 * @param {string} invoiceId - The invoice ID to download
 * @returns {Promise} API response with PDF redirect
 */
export const downloadInvoice = (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });

/**
 * Get usage statistics for the current user
 * @returns {Promise} API response with usage data
 */
export const getUsageStats = () => api.get('/dashboard/usage');

/**
 * Check if a user should upgrade based on their usage
 * @returns {Promise} API response with upgrade recommendations
 */
export const getUpgradeRecommendations = () => api.get('/dashboard/stats');

// Export all subscription functions as a named service
const subscriptionService = {
  getStatus,
  getPortalSession,
  createCheckoutSession,
  cancelSubscription,
  verifySession,
  getInvoices,
  downloadInvoice,
  getUsageStats,
  getUpgradeRecommendations
};

export default subscriptionService;
